import { Head, useForm } from '@inertiajs/react';
import { LoaderCircle } from 'lucide-react';
import { FormEventHandler } from 'react';

import InputError from '@/components/layout/InputError';
import TextLink from '@/components/layout/TextLink';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { useTranslations } from '@/hooks/useTranslations';
import AuthLayout from '@/layouts/auth-layout';

type LoginForm = {
    email: string;
    password: string;
    remember: boolean;
};

interface LoginProps {
    status?: string;
    canResetPassword: boolean;
    azureOnly?: boolean;
}

function MicrosoftLogo({ className }: { className?: string }) {
    return (
        <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" aria-hidden="true">
            <rect x="1" y="1" width="9" height="9" fill="#f25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
            <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
        </svg>
    );
}

export default function Login({ status, canResetPassword, azureOnly = false }: LoginProps) {
    const { t } = useTranslations();

    const { data, setData, post, processing, errors, reset } = useForm<Required<LoginForm>>({
        email: '',
        password: '',
        remember: false,
    });

    const submit: FormEventHandler = (e) => {
        e.preventDefault();
        post(route('login'), {
            onFinish: () => reset('password'),
        });
    };

    return (
        <AuthLayout title={t('auth.login.title')} description={t('auth.login.description')}>
            <Head title={t('auth.login.head_title')} />

            {status && <div className="text-success-subtle-foreground mb-4 text-center text-sm font-medium">{status}</div>}

            <div className="flex flex-col gap-4">
                <a
                    href={route('azure.redirect')}
                    className="group flex w-full items-center justify-center gap-3 rounded-lg border-2 border-[#0078d4] bg-[#0078d4] px-4 py-3 text-sm font-semibold text-white shadow-md transition-all duration-150 hover:bg-[#106ebe] hover:shadow-lg focus-visible:ring-2 focus-visible:ring-[#0078d4] focus-visible:ring-offset-2 focus-visible:outline-none active:scale-[0.98]"
                >
                    <MicrosoftLogo className="h-5 w-5 shrink-0" />
                    <span>{t('auth.login.microsoft_button')}</span>
                </a>

                {!azureOnly && (
                    <>
                        <div className="relative flex items-center gap-3">
                            <div className="bg-border h-px flex-1" />
                            <span className="text-muted-foreground text-xs">{t('auth.login.or_continue_with')}</span>
                            <div className="bg-border h-px flex-1" />
                        </div>

                        <form className="flex flex-col gap-5" onSubmit={submit}>
                            <div className="grid gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="email">{t('auth.login.email')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        required
                                        autoFocus
                                        tabIndex={1}
                                        autoComplete="email"
                                        value={data.email}
                                        onChange={(e) => setData('email', e.target.value)}
                                        placeholder="email@example.com"
                                    />
                                    <InputError message={errors.email} />
                                </div>

                                <div className="grid gap-2">
                                    <div className="flex items-center">
                                        <Label htmlFor="password">{t('auth.login.password')}</Label>
                                        {canResetPassword && (
                                            <TextLink href={route('password.request')} className="ml-auto text-sm" tabIndex={5}>
                                                {t('auth.login.forgot_password')}
                                            </TextLink>
                                        )}
                                    </div>
                                    <Input
                                        id="password"
                                        type="password"
                                        required
                                        tabIndex={2}
                                        autoComplete="current-password"
                                        value={data.password}
                                        onChange={(e) => setData('password', e.target.value)}
                                        placeholder="••••••••"
                                    />
                                    <InputError message={errors.password} />
                                </div>

                                <div className="flex items-center space-x-3">
                                    <Checkbox
                                        id="remember"
                                        name="remember"
                                        checked={data.remember}
                                        onClick={() => setData('remember', !data.remember)}
                                        tabIndex={3}
                                    />
                                    <Label htmlFor="remember">{t('auth.login.remember_me')}</Label>
                                </div>

                                <Button type="submit" className="w-full" tabIndex={4} disabled={processing}>
                                    {processing ? (
                                        <>
                                            <LoaderCircle className="h-4 w-4 animate-spin" />
                                            {t('auth.login.signing_in')}
                                        </>
                                    ) : (
                                        t('auth.login.sign_in')
                                    )}
                                </Button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </AuthLayout>
    );
}
